import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  LinearProgress,
  Chip,
  Card,
  CardContent,
  Stack,
  Divider,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import PeopleIcon from "@mui/icons-material/People";
import InsertChartIcon from "@mui/icons-material/InsertChart";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SchoolIcon from "@mui/icons-material/School";
import ApartmentIcon from "@mui/icons-material/Apartment";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import { ThemeColor } from "../../component/theme/color";
import { useDispatch, useSelector } from "react-redux";
import { GetDashboardDataAction } from "../../Redux/action/DashboardAction";
import {
  GetUniversityAction,
  GetOrganizationAction,
} from "../../Redux/action/SettingAction";
import { showSnackbar } from "../../Redux/action/SnackbarAction";
import { GetDashboardSummaryService } from "../../Redux/service/DashboardService";
import { GetDefaultIntakeService } from "../../Redux/service/SettingService";
import CustomSelect from "../../component/select field/OutlinedSelectField";
import {
  buildIntakeOptions,
  formatIntakeLabel,
  normaliseIntake,
} from "../../helper/intakeOptions";
import { FontFamily } from "../../component/theme/fontFamily";

const Root = styled("div")(({ theme }) => ({
  padding: theme.spacing(3),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
  },
}));

// One shared card look, so every tile on the page lines up and nothing draws
// more attention than the numbers inside it.
const cardSx = (accent) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  backgroundColor: "#FFFFFF",
  border: "1px solid #E2E8F0",
  borderRadius: "14px",
  boxShadow: "0 1px 3px rgba(15, 23, 42, 0.04)",
  position: "relative",
  overflow: "hidden",
  transition: "box-shadow 0.2s ease, border-color 0.2s ease",
  "&:hover": {
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
    borderColor: "#CBD5E1",
  },
  ...(accent && {
    "&::before": {
      content: '""',
      position: "absolute",
      top: 0,
      left: 0,
      width: "3px",
      height: "100%",
      backgroundColor: accent,
    },
  }),
});

/** Section title + optional right-hand note, on a single compact line. */
const SectionHeading = ({ title, note }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "baseline",
      justifyContent: "space-between",
      flexWrap: "wrap",
      gap: 1,
      mt: 1,
    }}
  >
    <Typography
      sx={{
        fontFamily: FontFamily.Poppins,
        fontSize: 15,
        fontWeight: 700,
        color: ThemeColor.NAVYBLUE,
      }}
    >
      {title}
    </Typography>
    {note && (
      <Typography sx={{ fontSize: 12, color: ThemeColor.GREY }}>{note}</Typography>
    )}
  </Box>
);

/**
 * Metric tile.
 *
 * The title is allowed two lines and every tile reserves the same height for
 * it, so a long label ("Applications (University, Sem)") is readable instead of
 * being cut off with an ellipsis, and the numbers still line up across a row.
 */
const MetricCard = ({
  title,
  value,
  hint,
  icon,
  color = ThemeColor.LIGHTPURPLE,
  loading,
}) => (
  <Card sx={cardSx(color)}>
    <CardContent sx={{ p: 2, flex: 1, "&:last-child": { pb: 2 } }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            fontFamily={FontFamily.Poppins}
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "#64748B",
              textTransform: "uppercase",
              letterSpacing: "0.4px",
              lineHeight: 1.35,
              minHeight: "2.7em", // two lines, so tiles stay aligned
              display: "flex",
              alignItems: "flex-start",
            }}
          >
            {title}
          </Typography>
          <Typography
            fontFamily={FontFamily.Poppins}
            sx={{
              fontSize: "1.85rem",
              fontWeight: 800,
              color: "#1E293B",
              lineHeight: 1.1,
            }}
          >
            {loading ? "…" : value !== null && value !== undefined ? value : "0"}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: "#94A3B8",
              lineHeight: 1.4,
              minHeight: "1.4em", // keeps tiles with and without a hint equal
            }}
          >
            {hint || ""}
          </Typography>
        </Stack>

        {icon && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: "12px",
              backgroundColor: `${color}14`,
              color,
              flexShrink: 0,
              "& svg": { fontSize: 22 },
            }}
          >
            {icon}
          </Box>
        )}
      </Stack>
    </CardContent>
  </Card>
);

// Colour + tone per metric column, so a number reads the same way in every table.
const COLUMN_TONES = [
  { match: ["approval"], bg: "rgba(16,185,129,0.10)", color: "#047857" },
  { match: ["reject"], bg: "rgba(239,68,68,0.10)", color: "#B91C1C" },
  { match: ["student"], bg: "rgba(14,165,233,0.10)", color: "#0369A1" },
  { match: ["application"], bg: "rgba(245,158,11,0.12)", color: "#92400E" },
];

const toneFor = (column) => {
  const lower = String(column).toLowerCase();
  return COLUMN_TONES.find((tone) => tone.match.some((m) => lower.includes(m)));
};

/**
 * Compact data card.
 *
 * Three things that were wrong before:
 *  - the sticky header was transparent, so rows scrolled *through* the column
 *    titles (visible on the university table);
 *  - alignment was decided per cell, so a column with mixed values came out
 *    ragged - it is now decided once per column;
 *  - an empty result rendered an empty card with no explanation.
 */
const DataCard = ({ title, subtitle, columns, rows }) => {
  const numericColumns = useMemo(() => {
    const numeric = new Set();
    columns.forEach((column) => {
      const hasNumber = rows.some((row) => typeof row[column] === "number");
      if (hasNumber) numeric.add(column);
    });
    return numeric;
  }, [columns, rows]);

  return (
    <Card sx={cardSx()}>
      <Box
        sx={{
          px: 2,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
        }}
      >
        <Typography
          sx={{
            fontFamily: FontFamily.Poppins,
            fontWeight: 700,
            fontSize: 13.5,
            color: "#1E293B",
            lineHeight: 1.3,
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography
            sx={{ color: ThemeColor.GREY, fontSize: 11.5, whiteSpace: "nowrap" }}
          >
            {subtitle}
          </Typography>
        )}
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflow: "auto", maxHeight: 300 }}>
        {rows.length === 0 ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography sx={{ color: "#94A3B8", fontSize: 13 }}>
              No data for this intake yet
            </Typography>
          </Box>
        ) : (
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column}
                    align={numericColumns.has(column) ? "right" : "left"}
                    sx={{
                      // Sticky cells need their own opaque background, otherwise
                      // the rows show through while scrolling.
                      backgroundColor: "#F8FAFC",
                      fontWeight: 700,
                      fontSize: 12,
                      color: "#475569",
                      textTransform: "uppercase",
                      letterSpacing: "0.3px",
                      borderBottom: "1px solid #E2E8F0",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    "&:hover": { backgroundColor: "rgba(21,83,158,0.04)" },
                    "&:last-child td": { borderBottom: 0 },
                  }}
                >
                  {columns.map((column) => {
                    const cell = row[column];
                    const tone = toneFor(column);
                    const isNumeric = numericColumns.has(column);

                    return (
                      <TableCell
                        key={column}
                        align={isNumeric ? "right" : "left"}
                        sx={{
                          fontSize: 13,
                          color: "#334155",
                          borderBottom: "1px solid #F1F5F9",
                          py: 1,
                        }}
                      >
                        {isNumeric && tone ? (
                          <Chip
                            label={cell ?? "-"}
                            size="small"
                            sx={{
                              height: 22,
                              minWidth: 40,
                              bgcolor: tone.bg,
                              color: tone.color,
                              fontWeight: 700,
                              fontSize: 12,
                            }}
                          />
                        ) : (
                          cell ?? "-"
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Box>
    </Card>
  );
};

const DashboardPage = () => {
  const dispatch = useDispatch();
  const dashboardReduxData = useSelector((state) => state.dashboard?.dashboardData);

  // Two independent sources feed the cards: the intake-scoped summary endpoint
  // and the aggregate dashboard endpoint in Redux. They used to race, each
  // calling setMetrics() and overwriting the other's numbers depending on which
  // request finished last. Each now owns its own slice and `metrics` is derived.
  const [summaryMetrics, setSummaryMetrics] = useState(null);
  const [aggregateMetrics, setAggregateMetrics] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);

  // Filters. `intake` starts as the configured default intake and can then be
  // changed from the dropdown; every card re-reads for the selected intake.
  const [intake, setIntake] = useState("");
  const [universityId, setUniversityId] = useState("");
  const [organizationId, setOrganizationId] = useState("");
  const [defaultIntakeResolved, setDefaultIntakeResolved] = useState(false);

  // If the configured default intake falls outside the generated year window,
  // add it — otherwise the Select would hold a value with no matching option and
  // render blank.
  const intakeOptions = useMemo(() => {
    const options = buildIntakeOptions();
    if (intake && !options.some((option) => option._id === intake)) {
      return [{ _id: intake, title: formatIntakeLabel(intake) }, ...options];
    }
    return options;
  }, [intake]);

  const universityListState = useSelector(
    (state) => state.setting?.university?.data?.universities
  );
  const organizationListState = useSelector(
    (state) => state.setting?.organization?.data?.organizations
  );

  const universities = useMemo(
    () =>
      (universityListState || [])
        .map(({ _id, name }) => ({ _id, title: name }))
        .sort((a, b) => (a.title || "").localeCompare(b.title || "")),
    [universityListState]
  );

  const organizations = useMemo(
    () =>
      (organizationListState || [])
        .map(({ _id, organizationName }) => ({ _id, title: organizationName }))
        .sort((a, b) => (a.title || "").localeCompare(b.title || "")),
    [organizationListState]
  );

  useEffect(() => {
    dispatch(GetDashboardDataAction());
    dispatch(GetUniversityAction({ page: 1, limit: 0 }));
    dispatch(GetOrganizationAction({ page: 1, limit: 0 }));
  }, [dispatch]);

  // Seed the intake filter from the configured default intake, once.
  useEffect(() => {
    const loadDefaultIntake = async () => {
      try {
        const def = await GetDefaultIntakeService();
        const configured = def?.data?.defaultIntake?.[0]?.intake || "";
        // Normalise so it matches the dropdown option values exactly, whatever
        // format it was saved in ("Fall-2026", "FALL 2026", …).
        if (configured) setIntake(normaliseIntake(configured));
      } catch (err) {
        console.error("Error loading default intake:", err);
      } finally {
        setDefaultIntakeResolved(true);
      }
    };

    loadDefaultIntake();
  }, []);

  // Reload whenever a filter changes. Waits for the default intake lookup so the
  // first paint is not an "all intakes" flash followed by the real numbers.
  useEffect(() => {
    if (!defaultIntakeResolved) return;

    let cancelled = false;

    const loadSummary = async () => {
      setSummaryLoading(true);
      try {
        // The service destructures an options object. Passing the bare string
        // meant `intake` came through as undefined, so no query params were
        // sent and the API answered 400 "Intake is required".
        const res = await GetDashboardSummaryService({
          intake,
          universityId,
          organizationId,
        });

        if (cancelled) return;

        setSummaryMetrics(res?.data?.data?.metrics || null);
      } catch (err) {
        if (cancelled) return;
        console.error("Error loading dashboard summary:", err);
        dispatch(showSnackbar("Failed to load dashboard summary", "error"));
      } finally {
        if (!cancelled) setSummaryLoading(false);
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  }, [intake, universityId, organizationId, defaultIntakeResolved, dispatch]);

  useEffect(() => {
    if (dashboardReduxData?.data) {
      const data = dashboardReduxData.data;

      // compute metrics from response
      const pending = Number(data?.applicantCountsByCommissionStatus?.pending || 0);
      const approved = Number(data?.applicantCountsByCommissionStatus?.approved || 0);
      const rejected = Number(data?.applicantCountsByCommissionStatus?.rejected || 0);
      const applications = pending + approved + rejected;

      const students = (data?.organisactionWithStudentCount || []).reduce((s, it) => s + (Number(it.studentCount) || 0), 0);

      // best-effort derived values; backend may need to provide specific fields for per-university or per-organization application/visa counts
      const totalStudentsSemester = Number(data?.studentsInIntake) || (data?.universitiesWithStudentCount || []).reduce((s, u) => s + (Number(u.studentCount) || 0), 0) || students;

      // pick a representative university (first) for per-university metrics if detailed fields exist
      const firstUniversity = (data?.universitiesWithStudentCount || [])[0] || null;

      setAggregateMetrics({
        applications,
        totalStudentsSemester,
        approved,
        rejected,
        students,
        universityApplications: firstUniversity?.applications ?? null,
        universityApprovals: firstUniversity?.approvals ?? null,
        organizationApplications: data?.organisactionWithApplicationCount?.total ?? null,
        organizationVisas: data?.organisactionWithVisaCount?.total ?? null,
      });
    } else if (dashboardReduxData && dashboardReduxData.message) {
      // error case
      dispatch(showSnackbar(dashboardReduxData.message, "error"));
    }
  }, [dashboardReduxData, dispatch]);

  // Single place where the cards are built. The intake-scoped summary wins when
  // it has a number; the aggregate endpoint fills the gaps; anything still
  // unknown renders as "-" rather than a misleading 0.
  const metrics = useMemo(() => {
    const s = summaryMetrics;
    const a = aggregateMetrics;

    const firstOf = (...values) => {
      for (const value of values) {
        if (value !== null && value !== undefined) return value;
      }
      return "-";
    };

    // These only carry a value once the matching filter is chosen, so say which
    // one rather than leaving a bare "-" the user cannot explain.
    const needsUniversity = universityId ? undefined : "Select a university";
    const needsOrganization = organizationId ? undefined : "Select an organization";
    const needsBoth =
      universityId && organizationId
        ? undefined
        : "Select a university and an organization";

    return [
      { title: "Applications (Semester)", value: firstOf(s?.totalApplicationsSubmittedInIntake, a?.applications), icon: <InsertChartIcon />, color: ThemeColor.BLUE },
      { title: "Students (Semester)", value: firstOf(s?.totalStudentsInIntake, a?.totalStudentsSemester), icon: <PeopleIcon />, color: ThemeColor.PURPLE },
      { title: "Visa Approvals (Semester)", value: firstOf(s?.totalVisasApprovedInIntake, a?.approved), icon: <CheckCircleIcon />, color: "#10B981" },
      // Now sourced from visaStatus. This used to read the *commission* status,
      // which counts something else entirely.
      { title: "Visa Rejections (Semester)", value: firstOf(s?.totalVisasRejectedInIntake), icon: <CancelIcon />, color: "#EF4444" },
      // No aggregate fallback on these five: the Redux dashboard endpoint is not
      // intake-scoped and reports the *first* university / all organizations, so
      // using it here would quietly show a number that answers a different
      // question. Better an honest "-" with the filter to pick.
      // `ThemeColor.ORANGE` does not exist - it used to render this tile with
      // "undefined" colours.
      { title: "Applications (University, Sem)", value: firstOf(s?.totalApplicationsInUniversity), hint: needsUniversity, icon: <SchoolIcon />, color: "#F97316" },
      { title: "Visa Approvals (University, Sem)", value: firstOf(s?.totalVisasApprovedForUniversity), hint: needsUniversity, icon: <CheckCircleIcon />, color: "#059669" },
      { title: "Students (Organization, Sem)", value: firstOf(s?.totalStudentsFromOrganization), hint: needsOrganization, icon: <ApartmentIcon />, color: ThemeColor.PURPLE },
      { title: "Applications (Organization, Sem)", value: firstOf(s?.totalApplicationsFromOrganization), hint: needsOrganization, icon: <InsertChartIcon />, color: "#F59E0B" },
      { title: "Visas (Organization, Sem)", value: firstOf(s?.totalVisasFromOrganization), hint: needsOrganization, icon: <AccountBalanceWalletIcon />, color: "#10B981" },
      { title: "Visas (Org → Univ, Sem)", value: firstOf(s?.totalVisasFromOrganizationForUniversity), hint: needsBoth, icon: <AccountBalanceWalletIcon />, color: ThemeColor.RED },
    ];
  }, [summaryMetrics, aggregateMetrics, universityId, organizationId]);

  const data = dashboardReduxData?.data || {};

  const agentStudentRows = (data?.organisactionWithStudentCount || []).map((o) => ({ Agent: o.organizationName, Students: o.studentCount }));

  const agentVisaApprovalRows = [
    { Agent: "Overall", Approvals: data?.applicantCountsByCommissionStatus?.approved ?? 0 },
  ];

  const agentVisaRejectRows = [
    { Agent: "Overall", Rejections: data?.applicantCountsByCommissionStatus?.rejected ?? 0 },
  ];

  const universityRows = (data?.universitiesWithStudentCount || []).map((u) => ({ University: u.name, Students: u.studentCount, State: u.state, City: u.city }));

  const intakeRows = [{ Intake: data?.intake ?? '—', Applications: data ? (data.applicantCountsByCommissionStatus?.pending ?? 0) + (data.applicantCountsByCommissionStatus?.approved ?? 0) + (data.applicantCountsByCommissionStatus?.rejected ?? 0) : '-', Approvals: data?.applicantCountsByCommissionStatus?.approved ?? '-' }];

  // Guard against a non-numeric card value ("-") and division by zero.
  const studentsValue = Number(metrics[1].value);
  const approvalsValue = Number(metrics[2].value);
  const conversionKnown =
    Number.isFinite(studentsValue) &&
    Number.isFinite(approvalsValue) &&
    studentsValue > 0;
  const conversionRatio = conversionKnown ? approvalsValue / studentsValue : 0;
  const conversionPercentage = conversionKnown
    ? `${Math.round(conversionRatio * 100)}%`
    : "-";

  // The tables below come from the aggregate endpoint, which is scoped to the
  // *configured* intake - not to the dropdown. Saying so beats the old
  // hardcoded "Last 30 days", which was simply not true: that endpoint applies
  // no date filter at all.
  const tableIntake = data?.intake ? formatIntakeLabel(data.intake) : null;
  const tableScope = tableIntake ? `Intake: ${tableIntake}` : "All intakes";
  const intakeMismatch = Boolean(
    intake && data?.intake && normaliseIntake(data.intake) !== intake
  );

  return (
    <Root>
      <Grid container spacing={2}>
        {/* Header & Filters */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              backgroundColor: "#FFFFFF",
              border: "1px solid #E2E8F0",
              borderRadius: "14px",
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={4}>
                <Typography
                  sx={{
                    fontFamily: FontFamily.Poppins,
                    fontSize: 21,
                    fontWeight: 800,
                    color: ThemeColor.NAVYBLUE,
                    lineHeight: 1.2,
                  }}
                >
                  Dashboard Overview
                </Typography>
                <Typography sx={{ color: ThemeColor.GREY, fontSize: 12.5, mt: 0.5 }}>
                  {summaryLoading
                    ? "Loading figures…"
                    : intake
                      ? `Showing figures for ${formatIntakeLabel(intake)}`
                      : "Showing figures across all intakes"}
                </Typography>
              </Grid>

              <Grid item xs={12} md={8}>
                <Grid container spacing={1.5}>
                  <Grid item xs={12} sm={4}>
                    <CustomSelect
                      label="Intake"
                      value={intake}
                      onChange={(e) => setIntake(e.target.value)}
                      option={intakeOptions}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <CustomSelect
                      label="University (Optional)"
                      value={universityId}
                      onChange={(e) => setUniversityId(e.target.value)}
                      option={universities}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <CustomSelect
                      label="Organization (Optional)"
                      value={organizationId}
                      onChange={(e) => setOrganizationId(e.target.value)}
                      option={organizations}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Overall / intake statistics */}
        <Grid item xs={12}>
          <SectionHeading
            title="Overall Statistics"
            note={intake ? formatIntakeLabel(intake) : "All intakes"}
          />
        </Grid>

        {metrics.slice(0, 4).map((m) => (
          <Grid item xs={12} sm={6} lg={3} key={m.title}>
            <MetricCard {...m} loading={summaryLoading} />
          </Grid>
        ))}

        {/* University-specific */}
        {universityId && (
          <>
            <Grid item xs={12}>
              <SectionHeading title="University-Specific Statistics" />
            </Grid>
            {metrics.slice(4, 6).map((m) => (
              <Grid item xs={12} sm={6} lg={3} key={m.title}>
                <MetricCard {...m} loading={summaryLoading} />
              </Grid>
            ))}
          </>
        )}

        {/* Organization-specific */}
        {organizationId && (
          <>
            <Grid item xs={12}>
              <SectionHeading title="Organization-Specific Statistics" />
            </Grid>
            {metrics.slice(6, 9).map((m) => (
              <Grid item xs={12} sm={6} lg={3} key={m.title}>
                <MetricCard {...m} loading={summaryLoading} />
              </Grid>
            ))}
          </>
        )}

        {/* Combined */}
        {universityId && organizationId && (
          <>
            <Grid item xs={12}>
              <SectionHeading title="Combined Statistics" />
            </Grid>
            <Grid item xs={12} sm={6} lg={3}>
              <MetricCard {...metrics[9]} loading={summaryLoading} />
            </Grid>
          </>
        )}

        {/* Segregations. Grouped so each row holds cards of similar size - the
            three summaries first, then the two wider breakdowns. */}
        <Grid item xs={12}>
          <SectionHeading
            title="Detailed Segregations"
            note={
              intakeMismatch
                ? `${tableScope} — these follow the default intake, not the filter above`
                : tableScope
            }
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={cardSx(ThemeColor.BLUE)}>
            <CardContent sx={{ p: 2, flex: 1, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" spacing={1.5} alignItems="flex-start">
                <Stack spacing={0.75} sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    fontFamily={FontFamily.Poppins}
                    sx={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: "#64748B",
                      textTransform: "uppercase",
                      letterSpacing: "0.4px",
                    }}
                  >
                    Conversion Percentage
                  </Typography>
                  <Typography
                    fontFamily={FontFamily.Poppins}
                    sx={{
                      fontSize: "1.85rem",
                      fontWeight: 800,
                      color: "#1E293B",
                      lineHeight: 1.1,
                    }}
                  >
                    {summaryLoading ? "…" : conversionPercentage}
                  </Typography>
                </Stack>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 44,
                    height: 44,
                    borderRadius: "12px",
                    backgroundColor: `${ThemeColor.BLUE}14`,
                    color: ThemeColor.BLUE,
                    flexShrink: 0,
                    "& svg": { fontSize: 22 },
                  }}
                >
                  <TrendingUpIcon />
                </Box>
              </Stack>

              {/* The bar and the counts underneath turn a lone percentage into
                  something readable, and fill what used to be a tall empty card. */}
              <LinearProgress
                variant="determinate"
                value={Math.min(100, Math.round(conversionRatio * 100))}
                sx={{
                  mt: 2,
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: "#E2E8F0",
                  "& .MuiLinearProgress-bar": {
                    borderRadius: 3,
                    backgroundColor: ThemeColor.BLUE,
                  },
                }}
              />

              <Stack
                direction="row"
                justifyContent="space-between"
                sx={{ mt: 1.25 }}
              >
                <Typography sx={{ fontSize: 12, color: "#475569" }}>
                  {conversionKnown ? `${approvalsValue} approved` : "Approvals —"}
                </Typography>
                <Typography sx={{ fontSize: 12, color: "#475569" }}>
                  {conversionKnown ? `of ${studentsValue} students` : "no students yet"}
                </Typography>
              </Stack>

              <Typography
                sx={{ color: "#94A3B8", fontSize: 11.5, mt: 1, lineHeight: 1.4 }}
              >
                Share of students in this intake who received visa approval.
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={4}>
          <DataCard
            title="Visa Approvals"
            subtitle={tableScope}
            columns={["Agent", "Approvals"]}
            rows={agentVisaApprovalRows}
          />
        </Grid>

        <Grid item xs={12} md={4}>
          <DataCard
            title="Visa Rejections"
            subtitle={tableScope}
            columns={["Agent", "Rejections"]}
            rows={agentVisaRejectRows}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <DataCard
            title="Organization-wise Student Count"
            subtitle={`${agentStudentRows.length} organizations`}
            columns={["Agent", "Students"]}
            rows={agentStudentRows}
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <DataCard
            title="Intake-wise Segregation"
            subtitle={tableScope}
            columns={["Intake", "Applications", "Approvals"]}
            rows={intakeRows}
          />
        </Grid>

        <Grid item xs={12}>
          <DataCard
            title="University-wise Segregation"
            subtitle={`${universityRows.length} universities`}
            columns={["University", "Students", "State", "City"]}
            rows={universityRows}
          />
        </Grid>
      </Grid>
    </Root>
  );
};

export default DashboardPage;
